import { AntSwitch, Checkbox, FormGroup, Icons, Input, Radio, RadioItem } from '@agro-club/agroclub-shared'
import { CompanyCommercial, Prepayment, USCompany } from 'modules/domain/company/types'
import { FormikFieldsType } from 'modules/domain/types'
import { ProfileType } from 'modules/domain/user/types'
import { endpoints } from 'modules/endpoints'
import { getPersonalNumberLabel } from 'modules/utils/numbers/formatPersonalNumber'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { FormMap, FormMapProps } from 'views/components/form/FormMap'
import { FormSelectButton } from 'views/components/FormSelectButtons/FormSelectButtons'
import FormSelectShared from 'views/components/FormSelectShared'
import { HrWithHeader } from 'views/components/Hr/Hr'
import { FormItemWrapper, ShortFormControl } from 'views/styled/common'
import { ShortNumberWrapper } from '../CompanyDetail/CompanyGeneral/CompanyRuDetailsModal'
import { AddContactModal } from './ContactForms/AddContactModal'

const CheckboxMarginWrapper = styled.div`
  margin-top: 4px;
  margin-left: 211px;
`

type Props = {
  formik: FormikFieldsType
  required?: boolean
}

const NameCompany: FC<Props & { withSignificant?: boolean }> = ({ formik, withSignificant = false }) => {
  const { t } = useTranslation('company')
  return (
    <div>
      <FormGroup label={t('common:company')} error={formik.errors.name} required>
        <Input {...formik.getFieldProps('name')} invalid={!!formik.errors.name} required />
      </FormGroup>
      <CheckboxMarginWrapper>
        {withSignificant && (
          <Checkbox
            isChecked={formik.values.is_significant}
            onChange={(_value, isChecked) => formik.setFieldValue('is_significant', isChecked)}
            label={t('significant')}
          />
        )}
      </CheckboxMarginWrapper>
    </div>
  )
}
const PersonalNumber: FC<Props> = ({ formik }) => {
  return (
    <ShortFormControl>
      <FormGroup label={getPersonalNumberLabel()} error={formik.errors.inn}>
        <Input {...formik.getFieldProps('inn')} invalid={!!formik.errors.inn} />
      </FormGroup>
    </ShortFormControl>
  )
}

const RegNumber: FC<Props> = ({ formik }) => {
  const { t } = useTranslation('company')
  return (
    <ShortFormControl>
      <FormGroup label={t('common:ie')} error={formik.errors.state_reg_number}>
        <Input {...formik.getFieldProps('state_reg_number')} invalid={!!formik.errors.state_reg_number} />
      </FormGroup>
    </ShortFormControl>
  )
}

const PrepaymentState: FC<Props> = ({ formik }) => {
  const { t } = useTranslation('company')
  return (
    <FormGroup label={t('form.fields.prepayment')} error={formik.errors.prepayment}>
      <FormItemWrapper>
        <Radio
          value={formik.values.prepayment}
          onChange={(val) => {
            formik.setFieldValue('prepayment', val)
          }}
        >
          <RadioItem value={Prepayment.yes} label={t('form.prepaymentValues.yes')} />
          <RadioItem value={Prepayment.no} label={t('form.prepaymentValues.no')} />
          <RadioItem value={Prepayment.unknown} label={t('form.prepaymentValues.unknown')} />
        </Radio>
      </FormItemWrapper>
    </FormGroup>
  )
}

const PaymentDelayDays: FC<Props> = ({ formik }) => {
  const { t } = useTranslation('company')
  return (
    <FormGroup label={t('form.fields.payment_delay_days')} error={formik.errors.payment_delay_days}>
      <ShortNumberWrapper>
        <Input
          {...formik.getFieldProps('payment_delay_days')}
          invalid={!!formik.errors.payment_delay_days}
          type="number"
          min={0}
        />
      </ShortNumberWrapper>
    </FormGroup>
  )
}

const CommercialField: FC<Props> = ({ formik }) => {
  const { t } = useTranslation('company')
  return (
    <FormGroup label={t('commercial')} error={formik.errors.commercial}>
      <FormItemWrapper>
        <Radio
          value={formik.values.commercial}
          onChange={(val) => {
            formik.setFieldValue('commercial', val)
          }}
        >
          <RadioItem value={CompanyCommercial.yes} label={t('common:yes')} />
          <RadioItem value={CompanyCommercial.no} label={t('common:no')} />
          <RadioItem value={CompanyCommercial.unknown} label={t('common:notSpecified')} />
        </Radio>
      </FormItemWrapper>
    </FormGroup>
  )
}

const ResponsibleField: FC<Props> = ({ formik, required }) => {
  const { t } = useTranslation('common')
  return (
    <FormSelectShared
      formik={formik}
      endpoint={endpoints.coordinatorsShort()}
      fieldName="responsible_coordinator"
      label={t('responsible')}
      searchField="search"
      getLabel={(dto) => dto.full_name}
      required={required}
      placeholder=""
    />
  )
}

const AddressField: FC<FormMapProps> = ({ disabled, handlerMap, children, required, formik, address }) => {
  return (
    <FormMap handlerMap={handlerMap} disabled={disabled} required={required} formik={formik} address={address}>
      {children}
    </FormMap>
  )
}

const SwitchFormWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 4px;
`

const initContactModal = { open: false, profileType: null, contactField: null }

const USCompanyResponsibleUsers: FC<Props & { isEdit?: boolean; company?: USCompany }> = ({
  formik,
  isEdit,
  company,
}) => {
  const { t } = useTranslation(['company', 'common'])
  const isIrelySyncedSeller = company?.is_synced_with_accounting_as_seller
  const isIrelySyncedBuyer = company?.is_synced_with_accounting_as_customer
  const [singleUser, setSingleUser] = useState(false)
  const [contactModal, setContactModal] = useState<{
    open: boolean
    profileType: ProfileType | null
    contactField: string | null
  }>(initContactModal)
  let endpointFunc: (types: ProfileType[], companyId?: string) => string | undefined
  let simpleApiFormat: boolean

  if (isEdit) {
    endpointFunc = (types: ProfileType[], companyId?: string) =>
      companyId && endpoints.activeEmployeesByTypes(companyId, types)
    simpleApiFormat = true
  } else {
    endpointFunc = (types: ProfileType[]) => endpoints.activeUsersByTypes(types)
    simpleApiFormat = false
  }
  const openModal = (profileType: ProfileType, contactField: string) =>
    setContactModal({
      open: true,
      profileType,
      contactField,
    })

  const switchOnChange = () => {
    formik.setFieldValue('responsible_seller', null)
    formik.setFieldValue('responsible_customer', null)
    formik.setFieldValue('merged_responsible', null)

    if (!isIrelySyncedSeller) {
      formik.setFieldValue('is_synced_with_accounting_as_seller', false)
    } else if (!isIrelySyncedBuyer) {
      formik.setFieldValue('is_synced_with_accounting_as_customer', false)
    }
    setSingleUser((prev) => !prev)
  }

  return (
    <>
      <HrWithHeader text={t('common:user')} />

      <SwitchFormWrapper>
        <AntSwitch checked={singleUser} onChange={switchOnChange} />
        {t('useSingleUser')}
      </SwitchFormWrapper>
      {singleUser ? (
        <>
          <FormSelectShared
            formik={formik}
            endpoint={endpointFunc([ProfileType.manager], company?.id)}
            fieldName="merged_responsible"
            label={t('sellerBuyer')}
            searchField="search"
            getLabel={(dto) => dto.profile.full_name}
            placeholder=""
            simpleApiFormat={simpleApiFormat}
          >
            <FormSelectButton
              eventId="addContact"
              icon={Icons.IconAdd}
              action={() => openModal(ProfileType.manager, 'merged_responsible')}
            />
          </FormSelectShared>
        </>
      ) : (
        <>
          <FormSelectShared
            formik={formik}
            endpoint={endpointFunc([ProfileType.seller, ProfileType.manager], company?.id)}
            fieldName="responsible_seller"
            label={t('seller')}
            searchField="search"
            getLabel={(dto) => dto.profile.full_name}
            placeholder=""
            simpleApiFormat={simpleApiFormat}
          >
            <FormSelectButton
              eventId="addContact"
              icon={Icons.IconAdd}
              action={() => openModal(ProfileType.seller, 'responsible_seller')}
            />
          </FormSelectShared>

          <FormSelectShared
            formik={formik}
            endpoint={endpointFunc([ProfileType.customer, ProfileType.manager], company?.id)}
            fieldName="responsible_customer"
            label={t('buyer')}
            searchField="search"
            getLabel={(dto) => dto.profile.full_name}
            placeholder=""
            simpleApiFormat={simpleApiFormat}
          >
            <FormSelectButton
              eventId="addContact"
              icon={Icons.IconAdd}
              action={() => openModal(ProfileType.customer, 'responsible_customer')}
            />
          </FormSelectShared>
        </>
      )}
      {contactModal.open && (
        <AddContactModal
          onClose={() => setContactModal(initContactModal)}
          initProfileType={contactModal.profileType as ProfileType}
          onSuccess={(res) => formik.setFieldValue(contactModal.contactField as string, res.id)}
          companyId={company?.id}
        />
      )}
    </>
  )
}

export const CommonFields = {
  NameCompany,
  PersonalNumber,
  RegNumber,
  PrepaymentState,
  PaymentDelayDays,
  CommercialField,
  ResponsibleField,
  AddressField,
  USCompanyResponsibleUsers,
}
